// src/components/AddProntuarioScreen.jsx
// Adicionar/Editar prontuário com validação, normalização de dados e UI premium responsiva.
// Entrada esperada:
// navigate("/prontuario/new", { state: { modo: "nova-evolucao", clienteId, nome, ultimaSessao, hasProntuario, numeroProntuario, prontuarioId } })
// navigate("/prontuario/edit", { state: { modo: "editar-evolucao", clienteId, nome, ultimaSessao, evolucaoId, dataAtendimento, resumoSessao, observacoes, recomendacoes, origem, automatica, sessaoId, numero } })

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  ArrowLeft,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  FileText,
  History,
  LoaderCircle,
  Menu,
  NotebookPen,
  Save,
  ShieldCheck,
  Sparkles,
  UserRound,
} from "lucide-react";

import AppSidebar from "../components/AppSidebar";
import { useToast } from "../context/ToastContext";
import Logo from "../assets/logoClaro.webp";

import "../sass/pages/addProntuario.scss";

const API_BASE = (
  process.env.REACT_APP_API_URL ||
  "https://api.psyassist.com.br"
).replace(/\/$/, "");

const AUTH_KEYS = {
  TOKEN: "psy_token",
  USER_NAME: "psy_usuario",
};

function readAuth(key) {
  if (key === AUTH_KEYS.TOKEN) {
    return (
      sessionStorage.getItem("psy_token") ||
      localStorage.getItem("psy_token") ||
      sessionStorage.getItem("token") ||
      localStorage.getItem("token") ||
      ""
    );
  }


  if (key === AUTH_KEYS.USER_NAME) {
    return (
      sessionStorage.getItem("psy_usuario") ||
      localStorage.getItem("psy_usuario") ||
      sessionStorage.getItem("nome") ||
      localStorage.getItem("nome") ||
      ""
    );
  }

  return sessionStorage.getItem(key) || localStorage.getItem(key) || "";
}

const saveAuthToken = (token) => {
  if (!token) return;
  localStorage.setItem(AUTH_KEYS.TOKEN, token);
  sessionStorage.setItem(AUTH_KEYS.TOKEN, token);
};

function onlyDigits(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function normalizeSpaces(value = "") {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function normalizeMultiline(value = "") {
  return String(value || "")
    .replace(/\r\n/g, "\n")
    .split("\n")
    .map((line) => line.replace(/[ \t]+/g, " ").trim())
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function formatDateBR(dateLike) {
  if (!dateLike) return "Não informado";

  try {
    const date =
      dateLike instanceof Date ? dateLike : new Date(String(dateLike));

    if (Number.isNaN(date.getTime())) return "Não informado";

    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(date);
  } catch {
    return "Não informado";
  }
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function isoDateOnly(dateLike) {
  if (!dateLike) return "";

  try {
    const value = String(dateLike);

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "";

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  } catch {
    return "";
  }
}

function isoToBR(dateValue) {
  const value = String(dateValue || "").trim();
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return "";

  return `${match[3]}/${match[2]}/${match[1]}`;
}

function createLocalNoonISO(dateValue) {
  const value = String(dateValue || "").trim();
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);

  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  const date = new Date(year, month - 1, day, 12, 0, 0, 0);

  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }

  return date.toISOString();
}

function isFutureISODate(dateValue) {
  if (!dateValue) return false;

  const selected = new Date(`${dateValue}T00:00:00`);
  const today = new Date(`${todayISO()}T00:00:00`);

  return selected > today;
}

function getWordCount(text) {
  return normalizeSpaces(text).split(" ").filter(Boolean).length;
}

function Field({
  label,
  required,
  hint,
  error,
  children,
  className = "",
  counter,
}) {
  return (
    <div className={`pr-field ${className} ${error ? "pr-field--error" : ""}`}>
      <div className="pr-field__top">
        <label className="pr-label">
          {label}
          {required ? <span className="pr-req">*</span> : null}
        </label>

        <div className="pr-field__meta">
          {counter ? <span className="pr-counter">{counter}</span> : null}
          {hint ? <span className="pr-hint">{hint}</span> : null}
        </div>
      </div>

      {children}

      {error ? (
        <div className="pr-err" role="alert">
          {error}
        </div>
      ) : null}
    </div>
  );
}

export default function AddProntuarioScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const pageRef = useRef(null);

  const state = location.state || {};

  const clienteId = state.clienteId || state._id || state.clientId || "";
  const nome = state.nome || state.nomeCliente || "Cliente";
  const ultimaSessao = state.ultimaSessao || "";

  const modo = String(state.modo || "").toLowerCase();
  const rotaEdicao = String(location.pathname || "").includes("/prontuario/edit");
  const isEditando = modo === "editar-evolucao" || rotaEdicao;
  const evolucaoId = isEditando
    ? state.evolucaoId || state._idEvolucao || state._idProntuario || ""
    : "";
  const prontuarioPrincipalId = state.prontuarioId || state.prontuarioPrincipalId || "";
  const resumoSessao = String(state.resumoSessao || "").trim();
  const origemEvolucao = String(state.origem || "").trim().toLowerCase();
  const sessaoId = state.sessaoId || "";
  const evolucaoAutomatica =
    state.automatica === true ||
    origemEvolucao === "sessao" ||
    Boolean(sessaoId);
  const isEvolucaoDaSessao =
    isEditando &&
    evolucaoAutomatica &&
    Boolean(resumoSessao);

  const hasProntuario = Boolean(
    state.hasProntuario ||
      state.temProntuario ||
      state.prontuarioAberto ||
      state.numeroProntuario ||
      state.prontuarioNumero ||
      prontuarioPrincipalId
  );

  const numeroProntuarioExistente =
    state.numeroProntuario || state.prontuarioNumero || state.numero || "";
  const totalEvolucoes = Number(state.totalEvolucoes || state.qtdeEvolucoes || 0);

  const tituloTela = isEvolucaoDaSessao
    ? "Complementar evolução"
    : isEditando
      ? "Editar evolução"
      : hasProntuario
      ? "Registrar nova evolução"
      : "Abrir prontuário";

  const tituloRegistro = isEvolucaoDaSessao
    ? "Complementar registro da sessão"
    : isEditando
      ? "Revisar lançamento clínico"
      : hasProntuario
      ? "Nova sessão do prontuário"
      : "Primeiro registro clínico";

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const salvarLabel = saving
    ? "Salvando..."
    : isEvolucaoDaSessao
      ? "Salvar complementação"
      : isEditando
        ? "Atualizar evolução"
      : hasProntuario
        ? "Salvar evolução"
        : "Salvar prontuário";
  const [errors, setErrors] = useState({});

  const [numero, setNumero] = useState("");
  const [dataAtendimento, setDataAtendimento] = useState("");
  const [observacoes, setObservacoes] = useState("");
  const [recomendacoes, setRecomendacoes] = useState("");
  const [motivoRetificacao, setMotivoRetificacao] = useState("");

  const nomeUsuario = useMemo(() => readAuth(AUTH_KEYS.USER_NAME) || "", []);

  const observacoesWords = useMemo(
    () => getWordCount(observacoes),
    [observacoes]
  );

  const recomendacoesWords = useMemo(
    () => getWordCount(recomendacoes),
    [recomendacoes]
  );

  const completion = useMemo(() => {
    const checks = [
      Boolean(clienteId),
      Boolean(dataAtendimento),
    ];

    if (isEvolucaoDaSessao) {
      checks.push(resumoSessao.length >= 10);
      if (observacoes) {
        checks.push(normalizeSpaces(observacoes).length >= 3);
      }
    } else {
      checks.push(normalizeSpaces(observacoes).length >= 10);
    }

    if (recomendacoes) checks.push(normalizeSpaces(recomendacoes).length >= 3);
    if (numero) checks.push(Number(onlyDigits(numero)) > 0);

    const total = checks.length || 1;
    const filled = checks.filter(Boolean).length;

    return Math.round((filled / total) * 100);
  }, [
    clienteId,
    dataAtendimento,
    isEvolucaoDaSessao,
    numero,
    observacoes,
    recomendacoes,
    resumoSessao,
  ]);

  const showMessage = useCallback(
    (typeOrPayload, text = "") => {
      const payload =
        typeof typeOrPayload === "object"
          ? typeOrPayload
          : {
              type: typeOrPayload,
              text,
            };

      const type = [
        "success",
        "warning",
        "error",
        "info",
      ].includes(payload.type)
        ? payload.type
        : "info";

      const defaultTitles = {
        success: "Registro concluído",
        warning: "Revise as informações",
        error: "Não foi possível concluir",
        info: "Informação",
      };

      toast[type]({
        title:
          payload.title ||
          defaultTitles[type],
        message:
          payload.text ||
          payload.message ||
          "",
        details: Array.isArray(
          payload.details
        )
          ? payload.details
          : [],
        actionLabel:
          payload.actionLabel || "",
        onAction:
          typeof payload.onAction ===
          "function"
            ? payload.onAction
            : null,
        duration:
          payload.duration !== undefined
            ? payload.duration
            : payload.actionLabel
              ? 0
              : 5200,
      });
    },
    [toast]
  );

  const focusFirstError = () => {
    window.setTimeout(() => {
      const root = pageRef.current || document;
      const errorElement = root.querySelector(".pr-err");
      const target =
        errorElement?.closest("label, .app-field, .pr-field, .rev-field, .al-form-card, .al-panel, section") ||
        errorElement;

      if (!target) return;

      target.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      const focusable = target.querySelector?.(
        "input, textarea, select, button:not([disabled])"
      );

      if (focusable && typeof focusable.focus === "function") {
        focusable.focus({ preventScroll: true });
      }
    }, 80);
  };

  useEffect(() => {
    if (!clienteId) {
      navigate("/clients", { replace: true });
      return;
    }

    if (isEditando) {
      setNumero(state.numero != null ? String(state.numero) : "");
      setDataAtendimento(
        state.dataAtendimento ? isoDateOnly(state.dataAtendimento) : ""
      );
      setObservacoes(state.observacoes || "");
      setRecomendacoes(state.recomendacoes || "");
      return;
    }

    setNumero(numeroProntuarioExistente ? String(numeroProntuarioExistente) : "");
    setDataAtendimento(todayISO());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId]);

  const setNumeroSafe = (value) => {
    setNumero(onlyDigits(value).slice(0, 9));
    setErrors((prev) => ({ ...prev, numero: "" }));
  };

  const setDataAtendimentoSafe = (value) => {
    setDataAtendimento(value);
    setErrors((prev) => ({ ...prev, dataAtendimento: "" }));
  };

  const setObservacoesSafe = (value) => {
    setObservacoes(value);
    setErrors((prev) => ({ ...prev, observacoes: "" }));
  };

  const setRecomendacoesSafe = (value) => {
    setRecomendacoes(value);
    setErrors((prev) => ({ ...prev, recomendacoes: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!clienteId) {
      nextErrors.clienteId = "Cliente não identificado.";
    }

    if (isEditando && !evolucaoId) {
      nextErrors.evolucaoId = "Evolução não identificada para edição.";
    }

    if (numero && Number(onlyDigits(numero)) <= 0) {
      nextErrors.numero = "Número do prontuário inválido.";
    }

    if (!dataAtendimento) {
      nextErrors.dataAtendimento = "Informe a data do atendimento.";
    } else if (!createLocalNoonISO(dataAtendimento)) {
      nextErrors.dataAtendimento = "Data inválida.";
    } else if (isFutureISODate(dataAtendimento)) {
      nextErrors.dataAtendimento = "A data do atendimento não pode ser futura.";
    }

    if (!isEvolucaoDaSessao) {
      if (!normalizeSpaces(observacoes)) {
        nextErrors.observacoes = "Informe as observações do atendimento.";
      } else if (normalizeSpaces(observacoes).length < 10) {
        nextErrors.observacoes =
          "As observações precisam ter pelo menos 10 caracteres.";
      }
    } else if (
      observacoes &&
      normalizeSpaces(observacoes).length < 3
    ) {
      nextErrors.observacoes =
        "Se informar uma complementação, use pelo menos 3 caracteres.";
    }

    if (recomendacoes && normalizeSpaces(recomendacoes).length < 3) {
      nextErrors.recomendacoes =
        "As recomendações precisam ter pelo menos 3 caracteres.";
    }

    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      showMessage("warning", "Revise os campos destacados antes de salvar.");
      focusFirstError();
      return false;
    }

    return true;
  };

  const buildPayload = () => {
    const body = {
      clienteId,
      dataAtendimento: createLocalNoonISO(dataAtendimento),
      observacoes: normalizeMultiline(observacoes),
      recomendacoes: normalizeMultiline(recomendacoes),
    };

    if (isEditando) body.motivoRetificacao = motivoRetificacao.trim();

    if (String(numero || "").trim() && !hasProntuario) {
      body.numero = Number(onlyDigits(numero));
    }

    return body;
  };

  const salvarProntuario = async () => {
    if (!validate()) return;
    if (isEditando && motivoRetificacao.trim().length < 5) {
      showMessage("warning", "Descreva o motivo da retificação (mínimo de 5 caracteres).");
      return;
    }

    const token = readAuth(AUTH_KEYS.TOKEN);

    if (!token) {
      showMessage({
        type: "warning",
        text: "Sessão inválida. Faça login novamente para salvar o prontuário.",
        actionLabel: "Ir para login",
        onAction: () => navigate("/login", { replace: true }),
      });
      return;
    }

    setSaving(true);

    try {
      const url = isEditando
        ? `${API_BASE}/psy/prontuario/${encodeURIComponent(evolucaoId)}`
        : `${API_BASE}/psy/prontuario`;
      const payload = buildPayload();

      const response = await fetch(url, {
        method: isEditando ? "PUT" : "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 401 || response.status === 403) {
        showMessage({
          type: "warning",
          text: "Sessão expirada. Faça login novamente para continuar.",
          actionLabel: "Ir para login",
          onAction: () => navigate("/login", { replace: true }),
        });
        return;
      }

      saveAuthToken(response.headers.get("x-new-token"));

      if (response.ok) {
        showMessage(
          "success",
          isEditando
            ? "Evolução atualizada com sucesso."
            : hasProntuario
              ? "Nova evolução registrada no prontuário."
              : "Prontuário aberto com sucesso."
        );

        const clientDataRetorno = state.clientData || {
          _id: clienteId,
          id: clienteId,
          nome,
          ultimaSessao,
        };

        navigate("/clients/details", {
          replace: true,
          state: {
            clientData: {
              ...clientDataRetorno,
              _id: clientDataRetorno?._id || clienteId,
              id: clientDataRetorno?.id || clienteId,
              nome: clientDataRetorno?.nome || nome,
              ultimaSessao: clientDataRetorno?.ultimaSessao || ultimaSessao,
            },
            refreshProntuarios: true,
          },
        });

        return;
      }

      const data = await response.json().catch(() => null);

      showMessage(
        "warning",
        data?.message || "Não foi possível salvar o prontuário."
      );
    } catch (error) {
      console.error("Erro ao salvar prontuário:", error);
      showMessage("warning", "Falha de comunicação com a API. Verifique sua conexão e tente novamente.");
    } finally {
      setSaving(false);
    }
  };

  const voltar = () => {
    if (saving) return;
    navigate(-1);
  };

  return (
    <div className="pr-page" ref={pageRef}>
      <div className="pr-bg" aria-hidden="true">
        <span className="pr-bg__orb pr-bg__orb--one" />
        <span className="pr-bg__orb pr-bg__orb--two" />
        <span className="pr-bg__orb pr-bg__orb--three" />
      </div>

      <AppSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

      <header className="pr-topbar">
        <button
          className="pr-icon-btn"
          type="button"
          onClick={() => setSidebarOpen(true)}
          aria-label="Abrir menu"
        >
          <Menu
            size={19}
            aria-hidden="true"
          />
        </button>

        <div className="pr-brand">
          <img src={Logo} alt="PsyAssist" className="pr-brand__logo" />
          <div className="pr-brand__text">
            <strong>{tituloTela}</strong>
            <span>
              Cliente: {nome} • {nomeUsuario || "Usuário"}
            </span>
          </div>
        </div>

        <div className="pr-topbar__actions">
          <button
            className="pr-btn pr-btn--ghost pr-btn--desktop"
            type="button"
            onClick={voltar}
            disabled={saving}
          >
            <ArrowLeft
              size={16}
              aria-hidden="true"
            />
            Voltar
          </button>

          <button
            className="pr-btn pr-btn--primary"
            type="button"
            onClick={salvarProntuario}
            disabled={saving}
          >
            {saving ? (
              <>
                <LoaderCircle
                  size={16}
                  aria-hidden="true"
                  className="pr-icon-spin"
                />
                Salvando...
              </>
            ) : (
              <>
                <Save
                  size={16}
                  aria-hidden="true"
                />
                {salvarLabel}
              </>
            )}
          </button>
        </div>
      </header>

      <main className="pr-main">
        <section className="pr-hero">
          <div>
            <span className="pr-kicker">
              <NotebookPen
                size={15}
                aria-hidden="true"
              />
              {isEditando
                ? "Edição clínica"
                : hasProntuario
                  ? "Evolução clínica"
                  : "Abertura clínica"}
            </span>

            <h1>{tituloTela}</h1>

            <p>
              O prontuário é único por cliente. Esta tela registra uma nova
              evolução clínica ou revisa um lançamento já existente, mantendo o
              histórico organizado na tela de detalhes.
            </p>
          </div>

          <aside className="pr-patient-card">
            <div className="pr-patient-card__avatar">
              <UserRound
                size={25}
                aria-hidden="true"
              />
            </div>

            <div className="pr-patient-card__content">
              <small>
                <UserRound
                  size={13}
                  aria-hidden="true"
                />
                Paciente
              </small>
              <strong>{nome}</strong>
              <span>
                <CalendarDays
                  size={14}
                  aria-hidden="true"
                />
                Última sessão:{" "}
                {formatDateBR(
                  ultimaSessao
                )}
              </span>
              <span>
                {hasProntuario
                  ? `Prontuário ${numeroProntuarioExistente || "já aberto"}`
                  : "Prontuário ainda não aberto"}
              </span>
            </div>
          </aside>
        </section>

        <section className="pr-mode-grid" aria-label="Fluxo do prontuário">
          <article className="pr-mode-card pr-mode-card--active">
            <div className="pr-mode-card__top">
              <span>
                <FileText
                  size={17}
                  aria-hidden="true"
                />
              </span>
              <small>Etapa 01</small>
            </div>

            <strong>{hasProntuario ? "Prontuário único" : "Abertura"}</strong>
            <p>
              {hasProntuario
                ? `Número preservado: ${numeroProntuarioExistente || "automático"}`
                : "Crie o prontuário principal do cliente."}
            </p>
          </article>

          <article className="pr-mode-card">
            <div className="pr-mode-card__top">
              <span>
                <NotebookPen
                  size={17}
                  aria-hidden="true"
                />
              </span>
              <small>Etapa 02</small>
            </div>

            <strong>{isEditando ? "Correção da evolução" : "Lançamento clínico"}</strong>
            <p>
              {isEditando
                ? "Atualize data, observações e recomendações da evolução selecionada."
                : "Registre a evolução da sessão atual dentro do prontuário."}
            </p>
          </article>

          <article className="pr-mode-card">
            <div className="pr-mode-card__top">
              <span>
                <History
                  size={17}
                  aria-hidden="true"
                />
              </span>
              <small>Etapa 03</small>
            </div>

            <strong>Histórico acessível</strong>
            <p>
              {totalEvolucoes > 0
                ? `${totalEvolucoes} evolução(ões) já vinculada(s) ao cliente.`
                : "Após salvar, o histórico aparece nos detalhes do cliente."}
            </p>
          </article>
        </section>

        <section className="pr-wrap">
          <div className="pr-workspace">
            <div
              className="pr-card"
              aria-busy={saving}
            >
            <div className="pr-card__head">
              <div>
                <span className="pr-card__eyebrow">
                  <ClipboardCheck
                    size={14}
                    aria-hidden="true"
                  />
                  Prontuário
                </span>
                <h2>{tituloRegistro}</h2>
                <p>
                  Campos com asterisco são obrigatórios. O número identifica o
                  prontuário principal; os próximos atendimentos entram como
                  evoluções no mesmo histórico.
                </p>
              </div>

              <div className="pr-score">
                <small>
                  <CheckCircle2
                    size={13}
                    aria-hidden="true"
                  />
                  Completude
                </small>
                <strong>{completion}%</strong>
                <div className="pr-score__bar">
                  <span style={{ width: `${completion}%` }} />
                </div>
              </div>
            </div>

            <div className="pr-grid">
              <Field
                label="Número do prontuário"
                hint={hasProntuario ? "Preservado no prontuário" : "Opcional"}
                error={errors.numero}
                className="pr-span-6"
              >
                <input
                  className="pr-input"
                  value={numero}
                  onChange={(event) => setNumeroSafe(event.target.value)}
                  placeholder={
                    hasProntuario && !isEditando
                      ? "Número preservado no prontuário"
                      : "Automático se ficar em branco"
                  }
                  inputMode="numeric"
                  disabled={hasProntuario}
                />
              </Field>

              <Field
                label="Data do atendimento"
                required
                hint={dataAtendimento ? isoToBR(dataAtendimento) : "Obrigatória"}
                error={errors.dataAtendimento}
                className="pr-span-6"
              >
                <input
                  className="pr-input"
                  type="date"
                  value={dataAtendimento}
                  onChange={(event) => setDataAtendimentoSafe(event.target.value)}
                  max={todayISO()}
                />
              </Field>

              {isEvolucaoDaSessao ? (
                <div className="pr-session-source pr-span-12">
                  <div className="pr-session-source__head">
                    <span>
                      <ClipboardCheck size={18} aria-hidden="true" />
                    </span>
                    <div>
                      <strong>Resumo clínico da sessão</strong>
                      <small>
                        Registro original preservado. A edição abaixo complementa
                        a evolução sem substituir o que foi informado no encerramento.
                      </small>
                    </div>
                  </div>

                  <div className="pr-session-source__content">
                    {resumoSessao}
                  </div>
                </div>
              ) : null}

              <Field
                label={isEvolucaoDaSessao ? "Complementação clínica" : "Observações"}
                required={!isEvolucaoDaSessao}
                hint={isEvolucaoDaSessao ? "Opcional" : "Registro clínico"}
                error={errors.observacoes}
                counter={`${observacoesWords} palavra(s)`}
                className="pr-span-12"
              >
                <textarea
                  className="pr-textarea pr-textarea--large"
                  value={observacoes}
                  onChange={(event) => setObservacoesSafe(event.target.value)}
                  onBlur={(event) =>
                    setObservacoesSafe(normalizeMultiline(event.target.value))
                  }
                  placeholder={
                    isEvolucaoDaSessao
                      ? "Adicione observações clínicas complementares, se necessário."
                      : "Descreva os principais pontos do atendimento, evolução, contexto e observações relevantes."
                  }
                  rows={7}
                />
              </Field>

              {isEditando ? (
                <Field label="Motivo da retificação" required hint="Registrado no histórico de auditoria" className="pr-span-12">
                  <textarea
                    className="pr-textarea"
                    value={motivoRetificacao}
                    onChange={(event) => setMotivoRetificacao(event.target.value.slice(0, 500))}
                    placeholder="Explique por que este registro está sendo corrigido."
                    rows={3}
                    maxLength={500}
                  />
                </Field>
              ) : null}

              <Field
                label="Recomendações"
                hint="Opcional"
                error={errors.recomendacoes}
                counter={`${recomendacoesWords} palavra(s)`}
                className="pr-span-12"
              >
                <textarea
                  className="pr-textarea"
                  value={recomendacoes}
                  onChange={(event) => setRecomendacoesSafe(event.target.value)}
                  onBlur={(event) =>
                    setRecomendacoesSafe(normalizeMultiline(event.target.value))
                  }
                  placeholder="Oriente encaminhamentos, combinados, tarefas ou pontos de acompanhamento."
                  rows={5}
                />
              </Field>
            </div>

            <div className="pr-actions" aria-label="Ações do prontuário">
              <button
                className="pr-btn pr-btn--ghost"
                type="button"
                onClick={voltar}
                disabled={saving}
              >
                <ArrowLeft
                  size={16}
                  aria-hidden="true"
                />
                Voltar
              </button>

              <button
                className="pr-btn pr-btn--primary"
                type="button"
                onClick={salvarProntuario}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <LoaderCircle
                      size={17}
                      aria-hidden="true"
                      className="pr-icon-spin"
                    />
                    Salvando...
                  </>
                ) : (
                  <>
                    <Save
                      size={17}
                      aria-hidden="true"
                    />
                    {salvarLabel}
                  </>
                )}
              </button>
            </div>
            </div>

            <aside className="pr-context-panel">
              <section className="pr-context-card pr-context-card--patient">
                <div className="pr-context-card__icon">
                  <UserRound
                    size={20}
                    aria-hidden="true"
                  />
                </div>

                <div>
                  <span>Paciente atual</span>
                  <strong>{nome}</strong>
                  <p>
                    {hasProntuario
                      ? `Prontuário ${
                          numeroProntuarioExistente ||
                          "já aberto"
                        }`
                      : "Primeiro registro clínico"}
                  </p>
                </div>
              </section>

              <section className="pr-context-card">
                <div className="pr-context-card__head">
                  <ShieldCheck
                    size={18}
                    aria-hidden="true"
                  />
                  <strong>
                    Registro responsável
                  </strong>
                </div>

                <ul>
                  <li>
                    Registre somente informações
                    necessárias ao acompanhamento.
                  </li>
                  <li>
                    Revise datas, observações e
                    recomendações antes de salvar.
                  </li>
                  <li>
                    Evite incluir dados sem relação
                    com a finalidade clínica.
                  </li>
                </ul>
              </section>

              <section className="pr-context-card pr-context-card--summary">
                <div className="pr-context-card__head">
                  <Sparkles
                    size={18}
                    aria-hidden="true"
                  />
                  <strong>
                    Resumo do lançamento
                  </strong>
                </div>

                <dl>
                  <div>
                    <dt>Modo</dt>
                    <dd>
                      {isEditando
                        ? "Edição"
                        : hasProntuario
                          ? "Nova evolução"
                          : "Abertura"}
                    </dd>
                  </div>

                  <div>
                    <dt>Atendimento</dt>
                    <dd>
                      {dataAtendimento
                        ? isoToBR(
                            dataAtendimento
                          )
                        : "Não informado"}
                    </dd>
                  </div>

                  <div>
                    <dt>Observações</dt>
                    <dd>
                      {observacoesWords} palavra(s)
                    </dd>
                  </div>

                  <div>
                    <dt>Completude</dt>
                    <dd>{completion}%</dd>
                  </div>
                </dl>
              </section>
            </aside>
          </div>
        </section>
      </main>
    </div>
  );
}
